import styled from '@emotion/styled';
import { H3, Text, TextLabel, SmallText } from 'ds/components/typography';
import { Button } from 'ds/components/button';
import { colorToAlpha } from 'helpers/insight';
import { SpacingScale } from 'ds/theme/spacing';

export const Root = styled.div<{ sidePaddingSpacing?: SpacingScale }>(({ theme, sidePaddingSpacing }) => ({
  display: 'flex',
  flexGrow: 1,
  alignItems: 'center',
  padding: theme.spacing(4, sidePaddingSpacing || 3),
  flexDirection: 'column',
  gap: theme.spacing(3),
  borderRadius: theme.borderRadius.small,
  border: `1px solid ${theme.colors.neutrals.grey6}`,
  boxShadow: theme.shadow.level8,
  backgroundColor: theme.colors.brand.lightBlue,
  marginBottom: theme.spacing(2),
  [theme.media.createObjectMediaQuery({ from: 3 })]: {
    '.terms-line': {
      maxWidth: '263px',
      textAlign: 'center',
    },
  },
}));

export const FormTitle = styled(H3.withComponent('div'))(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
}));

export const HintTitle = styled(Text)(({ theme }) => ({
  color: theme.colors.neutrals.grey3,
}));

export const AgentName = styled(Text)(({ theme }) => ({
  color: theme.colors.brand.main,
  textAlign: 'center',
}));

export const LogoWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  svg: {
    display: 'block',
  },
  '.agent-logo': {
    position: 'relative',
  },
}));

export const TextWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

export const ContactedWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.colors.neutrals.grey1,
  gap: theme.spacing(0.5),
}));

export const InfoBox = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.neutrals.grey11,
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(2, 0),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  gap: theme.spacing(1.5),
  a: {
    textDecoration: 'none',
  },
}));

export const AgentText = styled(SmallText.withComponent('span'))(({ theme }) => ({
  color: theme.colors.neutrals.grey1,
}));

export const AgentTextDivide = styled(AgentText)(({ theme }) => ({
  margin: theme.spacing(0, .5),
  '::after': {
    display: 'inline',
    content: '"•"',
  },
}));

export const PhoneButton = styled(Button.withComponent('a'))(({ theme }) => ({
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  svg: {
    marginLeft: theme.spacing(1),
  },
}));

export const FormBody = styled.div(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  input: {
    height: theme.spacing(6),
  },
}));

export const SubmitButton = styled.div(({ theme }) => ({
  borderRadius: theme.borderRadius.round,
  backgroundColor: theme.colors.neutrals.white,
  cursor: 'pointer',
  border: `1px solid ${theme.colors.neutrals.grey6}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '48px',
  height: '48px',
}));

export const OfficeLogo = styled.div<{ imageURL: string }>(({ theme, imageURL }) => ({
  width: '80px',
  height: '80px',
  border: `1px solid ${theme.colors.neutrals.grey8}`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  backgroundPosition: 'center center',
  backgroundImage: `url("${imageURL}")`,
}));

export const AgentLogo = styled.div<{ imageURL: string }>(({ theme, imageURL }) => ({
  width: '80px',
  height: '80px',
  position: 'relative',
  borderRadius: theme.borderRadius.round,
  border: `1px solid ${theme.colors.neutrals.grey8}`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
  backgroundColor: theme.colors.neutrals.grey6,
  backgroundImage: `url("${imageURL}")`,
}));

export const LeadingLabel = styled(TextLabel)(({ theme }) => ({
  position: 'absolute',
  color: theme.colors.brand.lighter,
  backgroundColor: theme.colors.brand.lightest,
  whiteSpace: 'nowrap',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.borderRadius.tiny,
  border: `1px solid ${colorToAlpha(theme.colors.brand.lighter, 0.2)}`,
  bottom: '-8px',
  zIndex: 12,
  left: '50%',
  transform: 'translateX(-50%)',
}));

export const LicenseLabel = TextLabel.withComponent('span');
